export const PLANNER_STATUSES = [
    { id: "idea", label: "Idea" },
    { id: "need", label: "To-Do" },
    { id: "quoted", label: "Quoted" },
    { id: "doing", label: "In progress" },
    { id: "blocked", label: "Blocked" },
    { id: "done", label: "Done" },
    { id: "dropped", label: "Dropped" },
];

export const PLANNER_PRIORITIES = [
    { id: "low", label: "Low" },
    { id: "normal", label: "Normal" },
    { id: "high", label: "High" },
    { id: "urgent", label: "Urgent" },
];

export const PLANNER_KINDS = [
    { id: "need", label: "Customer need" },
    { id: "idea", label: "Internal idea" },
    { id: "followup", label: "Follow-up" },
];

export const PLANNER_LINE_KINDS = [
    { id: "job", label: "Job", blurb: "The whole customer job. Set the estimate here, then build it from components." },
    { id: "component", label: "Component", blurb: "A station or deliverable — sanding cell, gripper, feeder, software package." },
    { id: "labor", label: "Labor", blurb: "Hours grouped by what the time is: design, install, software, R&D, PM." },
    { id: "purchase", label: "Purchase", blurb: "Bought parts and materials — robot, EOAT, pedestal, guarding, sensors." },
];

export const PLANNER_CHILD_LINE_KINDS = PLANNER_LINE_KINDS.filter((row) => row.id !== "job");

/** Distinct accents for job / component / labor / purchase so mixed lists are scannable. */
export function plannerKindAccent(theme, kind) {
    const map = {
        job: theme.palette.primary.main,
        component: theme.palette.primary.main,
        labor: theme.palette.warning.main,
        purchase: theme.palette.info.main,
    };
    return map[kind] || theme.palette.primary.main;
}

export const PLANNER_TONES = ["lime", "gold", "steel", "ice", "rose", "sand"];

export const DEFAULT_PLANNER_GROUPS = [
    { key: "robot-cell", label: "Robot cell", blurb: "Full cell, guarding, layout, and integration.", icon: "◎", tone: "lime", sortOrder: "0001" },
    { key: "existing-machine", label: "Existing machine integration", blurb: "Hooking the cell into machines they already have.", icon: "⚙", tone: "gold", sortOrder: "0002" },
    { key: "machine-tending", label: "Machine tending", blurb: "CNC load/unload, doors, vises, and cycle handoff.", icon: "⚙️", tone: "gold", sortOrder: "0003" },
    { key: "basic", label: "Basic", blurb: "First working scope — the version that ships and runs.", icon: "·", tone: "lime", sortOrder: "0004" },
    { key: "advanced", label: "Advanced", blurb: "Options, extras, and the next layer after basic is running.", icon: "✦", tone: "rose", sortOrder: "0005" },
    { key: "palletizing", label: "Palletizing", blurb: "Cases, layers, pallets, and end-of-line stacking.", icon: "▣", tone: "steel", sortOrder: "0006" },
    { key: "packaging", label: "Packaging", blurb: "Cartons, bags, labeling, and pack-out.", icon: "⌘", tone: "ice", sortOrder: "0007" },
    { key: "software", label: "Software component", blurb: "Viewers, programs, ERP hooks, and shop tools.", icon: "✦", tone: "rose", sortOrder: "0008" },
    { key: "inspection", label: "Inspection / CMM", blurb: "Gaging, CMM tend, and quality checks.", icon: "◎", tone: "sand", sortOrder: "0009" },
    { key: "safety", label: "Guarding / safety", blurb: "Fence, scanner, interlocks, and cell access.", icon: "▣", tone: "steel", sortOrder: "0010" },
    { key: "training", label: "Training", blurb: "Operators, maintenance, and handover.", icon: "📘", tone: "lime", sortOrder: "0011" },
    { key: "other", label: "Other", blurb: "Anything that does not fit a cell type yet.", icon: "·", tone: "steel", sortOrder: "0012" },
    { key: "rd-time", label: "R&D Time", blurb: "Research, prototyping, feasibility, and internal engineering time.", icon: "✦", tone: "ice", sortOrder: "0013" },
    { key: "installation", label: "Installation", blurb: "On-site install, commissioning, and customer handoff.", icon: "▣", tone: "gold", sortOrder: "0014" },
    { key: "eoat", label: "EOAT", blurb: "End-of-arm tooling — grippers, changers, sensors, and custom tooling.", icon: "◎", tone: "lime", sortOrder: "0015" },
    { key: "electrical-enclosure", label: "Electrical enclosure", blurb: "Panel build, I/O, drives, and cell wiring.", icon: "▣", tone: "steel", sortOrder: "0016" },
    { key: "robot", label: "Robot", blurb: "The arm itself — cobot or industrial, plus the controller.", icon: "◎", tone: "lime", sortOrder: "0017" },
    { key: "freight", label: "Freight / travel", blurb: "Shipping, travel, lodging, and on-site extras.", icon: "·", tone: "sand", sortOrder: "0018" },
];

export const PLANNER_SKELETONS = [
    {
        id: "shop-package",
        label: "Shop package",
        blurb: "First-cut components when a job covers integration, basic, advanced, and the rest.",
        branches: [
            { title: "Existing machine integration", lineKind: "component", groupKey: "existing-machine" },
            { title: "Basic", lineKind: "component", groupKey: "basic" },
            { title: "Advanced", lineKind: "component", groupKey: "advanced" },
            { title: "Guarding and safety", lineKind: "component", groupKey: "safety" },
            { title: "Software", lineKind: "component", groupKey: "software" },
            { title: "Training and handover", lineKind: "component", groupKey: "training" },
        ],
    },
    {
        id: "integration-split",
        label: "Integration split",
        blurb: "Break existing equipment into handshake, infeed, and the machine itself.",
        branches: [
            { title: "Existing machine integration", lineKind: "component", groupKey: "existing-machine" },
            { title: "Infeed / outfeed", lineKind: "component", groupKey: "machine-tending" },
            { title: "Controls and handshake", lineKind: "component", groupKey: "software" },
        ],
    },
    {
        id: "station-lines",
        label: "Station labor + buys",
        blurb: "Typical guess lines under a station: design, install, software, and a purchases bucket.",
        branches: [
            { title: "Design / engineering", lineKind: "labor", laborCategory: "design" },
            { title: "Installation", lineKind: "labor", laborCategory: "installation" },
            { title: "Software", lineKind: "labor", laborCategory: "software" },
            { title: "Purchases / materials", lineKind: "purchase" },
        ],
    },
    {
        id: "labor-split",
        label: "Labor split",
        blurb: "Hour buckets by rate — design, R&D, install, software, training, and PM.",
        branches: [
            { title: "Design / engineering", lineKind: "labor", laborCategory: "design" },
            { title: "R&D / feasibility", lineKind: "labor", laborCategory: "rd" },
            { title: "Installation", lineKind: "labor", laborCategory: "installation" },
            { title: "Custom software", lineKind: "labor", laborCategory: "software" },
            { title: "Training", lineKind: "labor", laborCategory: "training" },
            { title: "Project management", lineKind: "labor", laborCategory: "pm" },
        ],
    },
];

function clean(value) {
    return String(value || "").replace(/\s+/g, " ").trim();
}

export function slugifyGroup(label, existing = []) {
    const base =
        clean(label)
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "")
            .slice(0, 40) || "group";
    const taken = new Set((existing || []).map((row) => row.key));
    if (!taken.has(base)) return base;
    let i = 2;
    while (taken.has(`${base}-${i}`)) i += 1;
    return `${base}-${i}`;
}
